/**
 * MisPagos — vista cross-edificio del residente.
 *
 * Lista TODOS los pagos pendientes del residente en TODOS los edificios donde
 * tiene relación, agrupados por edificio → unidad. Permite seleccionar varios
 * y pagarlos en un único flujo (con N comprobantes si son distintos edificios).
 *
 * Reutiliza:
 *   - useSelectedPayments (estado del carrito)
 *   - PaymentCheckoutModal (modal multi-grupo)
 *   - paymentProviders (manualTransfer activo)
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { auth, db } from "../firebase";
import {
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { useBuilding } from "../context/BuildingContext";
import { useEffectiveIdentity } from "../context/EffectiveIdentityContext";
import { tolerantId } from "../utils/firestoreHelpers";
import {
  bidCanonico,
  edificiosQueAdministra,
  leerMiAcceso,
  leerPagosParaResidente,
} from "../utils/consultasEdificio";
import { useToast } from "../components/Toast";
import { useSelectedPayments } from "../services/paymentCart";
import PaymentCheckoutModal from "../components/PaymentCheckoutModal";
import {
  FaBuilding,
  FaChevronDown,
  FaChevronRight,
  FaWallet,
  FaCheckCircle,
  FaCalendarAlt,
  FaUserFriends,
} from "react-icons/fa";
import {
  colors,
  spacing,
  radius,
  shadows,
  typography,
  pageContainer,
} from "../utils/designTokens";
import { useIsMobile } from "../hooks/useResponsive";
import {
  AppScreen,
  AppHeader,
  AppCard,
  AppEmptyState,
  AppButton,
  IconBox,
  Badge,
  SectionLabel,
} from "../components/app";
import { appColors, appType, appRadius, appShadows, appSpacing } from "../utils/appTheme";
import { useTranslation } from "react-i18next";

const formatCurrencyUY = (n) =>
  "$ " + Number(n || 0).toLocaleString("es-UY", { maximumFractionDigits: 2 });

/**
 * Edificios que puede leer quien mira en "Ver como residente": los que
 * administra (user_access.staffBids). Con las reglas 0D una consulta con un
 * edificio ajeno se niega entera, así que la vista previa se acota a esos.
 * Devuelve null si no hay que filtrar (superadmin, o sin user_access todavía:
 * entonces manda lo de siempre).
 */
async function edificiosDelStaffQueMira() {
  return edificiosQueAdministra(await leerMiAcceso(db, auth.currentUser?.uid));
}

const MisPagos = ({ setSelected, goBack }) => {
  const { buildings, soloAlquilerEdificio } = useBuilding();
  const { email: effEmail, userid: effUserid, readOnly: viendoComo } = useEffectiveIdentity();
  const isMobile = useIsMobile();
  const { showToast } = useToast();
  const { t } = useTranslation("misPagos");
  const [loading, setLoading] = useState(false);
  const [allPayments, setAllPayments] = useState([]);
  const [tenantPayments, setTenantPayments] = useState([]);
  const [allApartments, setAllApartments] = useState([]);
  const [collapsed, setCollapsed] = useState({});
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [currentUserid, setCurrentUserid] = useState(null);
  // Toggle opcional para propietarios: incluir en la vista los pagos de sus
  // inquilinos (para los casos donde el alquiler incluye gastos comunes y el
  // propietario los paga). Off por default.
  const [includeTenantPayments, setIncludeTenantPayments] = useState(false);
  // "Ver como residente": edificios del staff que mira (Set de bids), null =
  // sin filtro, undefined = todavía no se sabe (no se consulta nada).
  const [edificiosQueMira, setEdificiosQueMira] = useState(viendoComo ? undefined : null);

  useEffect(() => {
    if (!viendoComo) {
      setEdificiosQueMira(null);
      return undefined;
    }
    let cancelado = false;
    setEdificiosQueMira(undefined);
    edificiosDelStaffQueMira()
      .catch((e) => {
        console.warn("[MisPagos] No se pudo leer user_access de quien mira:", e?.code || e?.message);
        return null;
      })
      .then((filtro) => {
        if (!cancelado) setEdificiosQueMira(filtro);
      });
    return () => {
      cancelado = true;
    };
  }, [viendoComo]);

  const buildingIds = useMemo(() => {
    const todos = (buildings || []).map((b) => Number(b.buildingid));
    if (!edificiosQueMira) return todos;
    return todos.filter((bid) => edificiosQueMira.has(bidCanonico(bid)));
  }, [buildings, edificiosQueMira]);

  // Resolver userid del residente actual a partir de su email Firebase Auth.
  // Probamos exact match + variante lowercase porque docs viejos pueden tener
  // el email con mayúsculas.
  useEffect(() => {
    let cancelled = false;
    const resolve = async () => {
      // "Ver como": el userid viene del contexto (quien mira es staff y con
      // C10 no lista users por email).
      if (viendoComo && effUserid != null && String(effUserid).trim() !== "") {
        setCurrentUserid(String(effUserid));
        return;
      }
      if (!effEmail) return;
      const tryEmails = [effEmail, effEmail.toLowerCase()];
      const uniq = Array.from(new Set(tryEmails));
      for (const candidateEmail of uniq) {
        const snap = await getDocs(
          query(collection(db, "users"), where("email", "==", candidateEmail))
        );
        if (cancelled) return;
        if (!snap.empty) {
          const data = snap.docs[0].data();
          setCurrentUserid(String(data.userid));
          return;
        }
      }
      console.warn("[MisPagos] No se encontró doc users para", effEmail);
    };
    resolve();
    return () => {
      cancelled = true;
    };
  }, [effEmail, effUserid, viendoComo]);

  // Cargar payments + apartments de todos los edificios del residente.
  const loadData = useCallback(async () => {
    if (edificiosQueMira === undefined) return; // "ver como": esperando los edificios de quien mira
    if (!currentUserid || buildingIds.length === 0) {
      setAllPayments([]);
      setAllApartments([]);
      return;
    }
    setLoading(true);
    try {
      // Pagos: una consulta por edificio (reglas 0D). En un edificio donde es
      // residente vigente trae todos los pagos y se filtra abajo; donde solo es
      // ex-ocupante, las reglas le dejan leer solo los suyos y la consulta se
      // repite fijando sus userid (leerPagosParaResidente). En "ver como" no
      // hay segundo intento: buildingIds ya son edificios del staff que mira.
      // Un edificio que falla no tapa a los demás.
      const uidSesion = viendoComo ? null : auth.currentUser?.uid || null;
      const porEdificio = await Promise.allSettled(
        buildingIds.map((bid) => leerPagosParaResidente(db, bid, { uid: uidSesion }))
      );
      const allP = [];
      let edificiosConError = 0;
      porEdificio.forEach((r, i) => {
        if (r.status === "fulfilled") {
          allP.push(...r.value);
        } else {
          edificiosConError += 1;
          console.warn(`[MisPagos] pagos del edificio ${buildingIds[i]} no disponibles:`, r.reason?.code || r.reason?.message);
        }
      });
      if (edificiosConError > 0) showToast(t("toast.loadError"), "error");

      // Unidades: Firestore "in" acepta máximo 30 valores; tandas de 10
      // edificios con sus variantes número/string (20 valores). La mayoría de
      // residentes tiene 1-3 edificios.
      const allA = [];
      for (let i = 0; i < buildingIds.length; i += 10) {
        const chunk = buildingIds.slice(i, i + 10).flatMap((bid) => tolerantId(bid));
        const aSnap = await getDocs(
          query(
            collection(db, "apartments"),
            where("building", "in", chunk)
          )
        );
        aSnap.docs.forEach((d) => allA.push({ id: d.id, ...d.data() }));
      }
      // Filtrar payments del residente actual SOLO por userid. Antes incluía
      // payments cuyo apartmentId coincidía con alguna unidad del residente,
      // pero eso traía pagos de OTROS residentes de las mismas unidades
      // (ej. payments del propietario cuando el residente solo es inquilino).
      const uidStr = String(currentUserid);
      const userPayments = allP.filter(
        (p) => String(p.userid) === uidStr
      );

      // Payments de inquilinos de unidades donde el residente es OWNER.
      // Solo accesibles vía toggle "Incluir pagos de mis inquilinos" — útil
      // cuando el alquiler incluye GC y el propietario los paga.
      const myOwnerApartmentIds = new Set(
        allA
          .filter(
            (apt) =>
              String(apt.owner) === uidStr ||
              String(apt.ownerSecondary) === uidStr
          )
          .map((apt) => apt.id)
      );
      const tenantPays = allP.filter((p) => {
        if (String(p.userid) === uidStr) return false; // los propios ya están
        if (!p.apartmentId) return false; // legacy sin apartmentId: no discriminamos
        return myOwnerApartmentIds.has(p.apartmentId);
      });

      // Sin el nombre del inquilino: un residente no lee fichas ajenas de
      // users (C10). La etiqueta dice "tu inquilino" junto a la unidad.
      setAllPayments(userPayments);
      setTenantPayments(tenantPays);
      setAllApartments(allA);
    } catch (err) {
      console.error("[MisPagos] loadData fallo:", err);
      showToast(t("toast.loadError"), "error");
    } finally {
      setLoading(false);
    }
  }, [currentUserid, buildingIds, edificiosQueMira, viendoComo, showToast, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Pendientes propios.
  const ownPending = useMemo(
    () => allPayments.filter((p) => !p.paid && p.status !== "rechazado"),
    [allPayments]
  );
  // Pendientes de los inquilinos del residente (solo cuando el toggle está on).
  const tenantPending = useMemo(
    () =>
      tenantPayments.filter((p) => !p.paid && p.status !== "rechazado"),
    [tenantPayments]
  );
  // Lista combinada que se muestra/selecciona. Marcamos los del inquilino
  // con `__isTenantPayment` para la etiqueta visual.
  const pendingPayments = useMemo(() => {
    const base = ownPending.map((p) => ({ ...p, __isTenantPayment: false }));
    if (!includeTenantPayments) return base;
    return [
      ...base,
      ...tenantPending.map((p) => ({ ...p, __isTenantPayment: true })),
    ];
  }, [ownPending, tenantPending, includeTenantPayments]);
  // El toggle se muestra si existen payments de inquilinos en CUALQUIER estado
  // (no solo pendientes) — así el propietario sabe que existe la opción
  // aunque los pagos del inquilino ya estén en verificación.
  const hasAnyTenantPayments = tenantPayments.length > 0;

  const cart = useSelectedPayments(pendingPayments);

  // Agrupar por edificio → unidad para render.
  const groupedView = useMemo(() => {
    const byBuilding = new Map();
    const buildingById = new Map();
    (buildings || []).forEach((b) =>
      buildingById.set(String(b.buildingid), b)
    );
    pendingPayments.forEach((p) => {
      const bKey = String(p.buildingid);
      if (!byBuilding.has(bKey)) {
        const b = buildingById.get(bKey);
        byBuilding.set(bKey, {
          buildingId: p.buildingid,
          buildingName: b?.name || t("buildingFallback", { id: bKey }),
          payments: [],
          total: 0,
        });
      }
      const g = byBuilding.get(bKey);
      g.payments.push(p);
      const r = Number(p.remainingAmount);
      g.total += !isNaN(r) && r > 0 ? r : Number(p.amount) || 0;
    });
    return Array.from(byBuilding.values()).sort((a, b) =>
      (a.buildingName || "").localeCompare(b.buildingName || "")
    );
  }, [pendingPayments, buildings, t]);

  const toggleCollapsed = (bKey) =>
    setCollapsed({ ...collapsed, [bKey]: !collapsed[bKey] });

  const resolveAptForPayment = useCallback(
    (pago) => {
      if (pago.apartmentId) {
        return allApartments.find((a) => a.id === pago.apartmentId) || null;
      }
      // Legacy: buscar apartment por owner/tenant.
      const uidStr = String(currentUserid);
      return (
        allApartments.find(
          (a) =>
            String(a.owner) === uidStr ||
            String(a.ownerSecondary) === uidStr ||
            String(a.tenant) === uidStr ||
            String(a.tenantSecondary) === uidStr
        ) || null
      );
    },
    [allApartments, currentUserid]
  );

  const rolForPayment = useCallback(
    (pago) => {
      const apt = resolveAptForPayment(pago);
      if (!apt) return null;
      const uidStr = String(currentUserid);
      if (
        String(apt.owner) === uidStr ||
        String(apt.ownerSecondary) === uidStr
      )
        return "Propietario";
      if (
        String(apt.tenant) === uidStr ||
        String(apt.tenantSecondary) === uidStr
      )
        return "Inquilino";
      return null;
    },
    [resolveAptForPayment, currentUserid]
  );

  // Traduce el enum de rol ("Propietario"/"Inquilino") a la etiqueta visible.
  const roleLabel = (r) =>
    r === "Propietario"
      ? t("role.owner")
      : r === "Inquilino"
      ? t("role.tenant")
      : "";

  // ─── Contenido mobile: estilo app "Mis Pagos" ──────────────────────────────
  // Derivaciones SOLO de presentación (read-only) a partir de `groupedView`,
  // ya calculado arriba. No agregan queries ni efectos.
  const mobileTotalPending = groupedView.reduce((acc, g) => acc + (g.total || 0), 0);
  const mobilePaymentCount = groupedView.reduce(
    (acc, g) => acc + g.payments.length,
    0
  );

  const mobileContent = (
    <AppScreen
      header={
        <AppHeader
          title={t("title")}
          subtitle={t("subtitleMobile")}
          onBack={goBack}
        />
      }
    >
      {/* Tarjeta resumen: total pendiente + conteo (info que el header no da).
          El título/subtítulo viven solo en el AppHeader; acá no se repiten. */}
      {!loading && groupedView.length > 0 && (
        <AppCard padding={16} style={{ marginBottom: appSpacing.section }}>
          <div
            style={{
              background: appColors.cream,
              borderRadius: appRadius.tile,
              padding: "14px 16px",
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "space-between",
              gap: 12,
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div style={{ ...appType.statLabel, color: appColors.ink3, marginBottom: 4 }}>
                {t("totalPending")}
              </div>
              <div style={{ ...appType.stat, color: appColors.red, fontVariantNumeric: "tabular-nums" }}>
                {formatCurrencyUY(mobileTotalPending)}
              </div>
            </div>
            <div style={{ textAlign: "right", flexShrink: 0 }}>
              <div style={{ ...appType.statLabel, color: appColors.ink3, marginBottom: 4 }}>
                {t("paymentsLabel")}
              </div>
              <div style={{ ...appType.body, fontSize: 15, color: appColors.ink, fontVariantNumeric: "tabular-nums" }}>
                {mobilePaymentCount}
              </div>
            </div>
          </div>
        </AppCard>
      )}

      {/* Toggle opcional: incluir pagos de inquilinos (propietarios) */}
      {hasAnyTenantPayments && (
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            cursor: "pointer",
            background: appColors.surface2,
            padding: "12px 14px",
            borderRadius: appRadius.tile,
            marginBottom: appSpacing.section,
            minWidth: 0,
          }}
        >
          <IconBox icon={<FaUserFriends />} color={appColors.amber} size={34} />
          <span style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0, flex: 1 }}>
            <span style={{ ...appType.body, fontSize: 13.5, overflowWrap: "anywhere" }}>
              {t("includeTenantPayments")}
            </span>
            <span style={{ ...appType.caption, overflowWrap: "anywhere" }}>
              {t("includeTenantPaymentsHint")}
            </span>
          </span>
          <input
            type="checkbox"
            checked={includeTenantPayments}
            onChange={(e) => setIncludeTenantPayments(e.target.checked)}
            style={{ flexShrink: 0 }}
          />
        </label>
      )}

      {loading ? (
        <p style={{ ...appType.body, color: appColors.ink2, textAlign: "center", padding: 24 }}>
          {t("loading")}
        </p>
      ) : groupedView.length === 0 ? (
        <AppEmptyState
          icon={<FaCheckCircle />}
          title={t("emptyTitle")}
          description={t("emptyDescriptionMobile")}
        />
      ) : (
        <>
          <SectionLabel style={{ marginBottom: 12 }}>
            {t("yourBuildings", { count: groupedView.length })}
          </SectionLabel>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {groupedView.map((g) => {
              const bKey = String(g.buildingId);
              const isCollapsed = collapsed[bKey];
              return (
                <AppCard key={bKey} padding={0} style={{ overflow: "hidden" }}>
                  {/* Cabecera del grupo: IconBox edificio + nombre + conteo + total rojo */}
                  <button
                    type="button"
                    onClick={() => toggleCollapsed(bKey)}
                    style={{
                      width: "100%",
                      background: "transparent",
                      border: "none",
                      padding: "14px 16px",
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      cursor: "pointer",
                    }}
                  >
                    <IconBox icon={<FaBuilding />} size={40} />
                    <span style={{ display: "flex", flexDirection: "column", gap: 3, minWidth: 0, flex: 1, textAlign: "left" }}>
                      <span
                        style={{
                          ...appType.cardTitle,
                          fontSize: 15,
                          minWidth: 0,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {g.buildingName}
                      </span>
                      <span style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                        <span style={{ ...appType.statLabel, color: appColors.ink3 }}>
                          {t("paymentCount", { count: g.payments.length })}
                        </span>
                      </span>
                    </span>
                    <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 3, flexShrink: 0 }}>
                      <span style={{ ...appType.statLabel, color: appColors.ink3 }}>{t("total")}</span>
                      <span
                        style={{
                          ...appType.body,
                          fontSize: 15,
                          color: appColors.red,
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {formatCurrencyUY(g.total)}
                      </span>
                    </span>
                    <span style={{ flexShrink: 0, display: "inline-flex", color: appColors.ink3 }}>
                      {isCollapsed ? <FaChevronRight /> : <FaChevronDown />}
                    </span>
                  </button>

                  {/* Línea de estado coloreada (rojo: hay deuda pendiente) */}
                  {!isCollapsed && (
                    <div style={{ height: 2, background: appColors.red, opacity: 0.5 }} />
                  )}

                  {!isCollapsed && (
                    <div>
                      {g.payments.map((pago) => {
                        const apt = resolveAptForPayment(pago);
                        const rol = pago.__isTenantPayment ? "Inquilino" : rolForPayment(pago);
                        const tenantName = pago.__isTenantPayment
                          ? t("yourTenant")
                          : null;
                        const remaining = Number(pago.remainingAmount);
                        const due = !isNaN(remaining) && remaining > 0 ? remaining : Number(pago.amount) || 0;
                        const selected = cart.isSelected(pago.id);
                        return (
                          <label
                            key={pago.id}
                            style={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 12,
                              padding: "13px 16px",
                              borderTop: `1px solid ${appColors.line2}`,
                              background: selected
                                ? appColors.cream
                                : "transparent",
                              cursor: "pointer",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={selected}
                              onChange={() => cart.toggle(pago.id)}
                              style={{ flexShrink: 0, marginTop: 3 }}
                            />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ ...appType.body, fontSize: 14, overflowWrap: "anywhere" }}>
                                {pago.description}
                              </div>

                              {/* Chips de contexto: rol/unidad + pago de inquilino */}
                              {(apt || tenantName) && (
                                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
                                  {apt && (
                                    <Badge tone={rol === "Inquilino" ? "amber" : "blue"}>
                                      {t("unit", { number: apt.number })}
                                      {rol ? ` · ${roleLabel(rol)}` : ""}
                                    </Badge>
                                  )}
                                  {tenantName && (
                                    <Badge tone="amber" dot>
                                      {t("paymentFrom", { name: tenantName })}
                                    </Badge>
                                  )}
                                </div>
                              )}

                              {/* Footer estructurado: VENCE + IMPORTE */}
                              <div
                                style={{
                                  display: "grid",
                                  gridTemplateColumns: pago.maturity ? "1fr auto" : "auto",
                                  gap: 12,
                                  marginTop: 10,
                                  alignItems: "flex-end",
                                }}
                              >
                                {pago.maturity && (
                                  <div style={{ minWidth: 0 }}>
                                    <div style={{ ...appType.statLabel, color: appColors.ink3, marginBottom: 2, display: "flex", alignItems: "center", gap: 5 }}>
                                      <FaCalendarAlt size={9} /> {t("dueLabel")}
                                    </div>
                                    <div style={{ ...appType.body, fontSize: 13, fontVariantNumeric: "tabular-nums", overflowWrap: "anywhere" }}>
                                      {pago.maturity}
                                    </div>
                                  </div>
                                )}
                                <div style={{ minWidth: 0, textAlign: "right" }}>
                                  <div style={{ ...appType.statLabel, color: appColors.ink3, marginBottom: 2 }}>
                                    {t("amountLabel")}
                                  </div>
                                  <div style={{ ...appType.body, fontSize: 15, fontVariantNumeric: "tabular-nums" }}>
                                    {formatCurrencyUY(due)}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </AppCard>
              );
            })}
          </div>
        </>
      )}
    </AppScreen>
  );

  return (
    <>
    {isMobile ? mobileContent : (
    <div style={pageContainer}>
      <div style={styles.headerCard}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={styles.headerIcon}>
            <FaWallet />
          </div>
          <div style={{ flex: 1 }}>
            <h1 style={styles.title}>{t("title")}</h1>
            <p style={styles.subtitle}>
              {soloAlquilerEdificio ? t("subtitleDesktopRent") : t("subtitleDesktop")}
            </p>
          </div>
          {hasAnyTenantPayments && (
            <label
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                fontSize: 13,
                color: colors.gray700,
                cursor: "pointer",
                background: colors.bgMain,
                padding: "8px 12px",
                borderRadius: radius.sm,
              }}
            >
              <input
                type="checkbox"
                checked={includeTenantPayments}
                onChange={(e) => setIncludeTenantPayments(e.target.checked)}
              />
              {t("includeTenantPayments")}
            </label>
          )}
        </div>
      </div>

      {loading && (
        <p style={{ color: colors.gray600, fontSize: 14 }}>{t("loading")}</p>
      )}

      {!loading && groupedView.length === 0 && (
        <div style={styles.emptyState}>
          <FaCheckCircle style={{ fontSize: 32, color: colors.success }} />
          <p style={{ margin: 0, fontSize: 14, color: colors.gray700 }}>
            {t("emptyDescriptionDesktop")}
          </p>
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: spacing[4] }}>
        {groupedView.map((g) => {
          const bKey = String(g.buildingId);
          const isCollapsed = collapsed[bKey];
          return (
            <div key={bKey} style={styles.buildingCard}>
              <button
                type="button"
                onClick={() => toggleCollapsed(bKey)}
                style={styles.buildingHeader}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  {isCollapsed ? <FaChevronRight /> : <FaChevronDown />}
                  <FaBuilding style={{ color: colors.primary }} />
                  <span style={{ fontSize: 15, color: colors.black }}>
                    {g.buildingName}
                  </span>
                  <span style={styles.countBadge}>{g.payments.length}</span>
                </div>
                <span style={{ fontSize: 15, color: colors.error }}>
                  {formatCurrencyUY(g.total)}
                </span>
              </button>
              {!isCollapsed && (
                <div style={styles.paymentsTable}>
                  {g.payments.map((pago) => {
                    const apt = resolveAptForPayment(pago);
                    const rol = pago.__isTenantPayment
                      ? "Inquilino"
                      : rolForPayment(pago);
                    const tenantName = pago.__isTenantPayment
                      ? t("yourTenant")
                      : null;
                    const remaining = Number(pago.remainingAmount);
                    const due =
                      !isNaN(remaining) && remaining > 0
                        ? remaining
                        : Number(pago.amount) || 0;
                    return (
                      <label
                        key={pago.id}
                        style={{
                          ...styles.paymentRow,
                          background: pago.__isTenantPayment
                            ? "#fff7ed"
                            : "transparent",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={cart.isSelected(pago.id)}
                          onChange={() => cart.toggle(pago.id)}
                          style={{ marginRight: 12 }}
                        />
                        <div style={styles.paymentDescription}>
                          <span style={{ fontSize: 14, color: colors.gray900 }}>
                            {pago.description}
                          </span>
                          {apt && (
                            <span style={{ fontSize: 11, color: colors.gray600 }}>
                              {t("unit", { number: apt.number })} · {roleLabel(rol) || "—"}
                              {pago.__isTenantPayment && tenantName && (
                                <span style={{ marginLeft: 6, color: "#c2410c" }}>
                                  · {t("paymentFrom", { name: tenantName })}
                                </span>
                              )}
                            </span>
                          )}
                          {pago.maturity && (
                            <span style={{ fontSize: 11, color: colors.gray600 }}>
                              {t("dueOn", { date: pago.maturity })}
                            </span>
                          )}
                        </div>
                        <span style={{ fontSize: 14, color: colors.gray900 }}>
                          {formatCurrencyUY(due)}
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

    </div>
    )}

      {cart.count > 0 && (
        <div
          style={
            isMobile
              ? {
                  position: "fixed",
                  bottom: "calc(100px + env(safe-area-inset-bottom, 0px))",
                  left: "50%",
                  transform: "translateX(-50%)",
                  width: "calc(100% - 32px)",
                  maxWidth: 448,
                  background: appColors.surface,
                  borderRadius: appRadius.pill,
                  boxShadow: appShadows.hero,
                  padding: "10px 12px 10px 18px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                  zIndex: 50,
                  fontFamily: appType.fontFamily,
                }
              : styles.cartBar
          }
        >
          <div style={{ minWidth: 0 }}>
            <span style={{ ...(isMobile ? appType.bodySm : { fontSize: 14, color: colors.gray700 }) }}>
              {t("paymentCount", { count: cart.count })}
            </span>
            <span style={{ marginLeft: 10, ...(isMobile ? { ...appType.body, fontSize: 15, fontVariantNumeric: "tabular-nums" } : { fontSize: 16, color: colors.black }) }}>
              {formatCurrencyUY(cart.totalAmount)}
            </span>
          </div>
          {isMobile ? (
            <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
              <AppButton variant="ghost" onClick={cart.clear} style={{ padding: "10px 12px" }}>
                {t("clear")}
              </AppButton>
              <AppButton variant="primary" onClick={() => setCheckoutOpen(true)} style={{ padding: "10px 18px" }}>
                {t("pay")}
              </AppButton>
            </div>
          ) : (
            <div style={{ display: "flex", gap: 8 }}>
              <button type="button" onClick={cart.clear} style={styles.btnGhost}>
                {t("clear")}
              </button>
              <button type="button" onClick={() => setCheckoutOpen(true)} style={styles.btnPrimary}>
                {t("pay")}
              </button>
            </div>
          )}
        </div>
      )}

      <PaymentCheckoutModal
        open={checkoutOpen}
        onClose={() => setCheckoutOpen(false)}
        payments={cart.selectedPayments}
        buildings={buildings}
        showToast={showToast}
        onSuccess={() => {
          cart.clear();
          loadData();
        }}
      />
    </>
  );
};

const styles = {
  headerCard: {
    background: colors.bgCard,
    borderRadius: radius.md,
    padding: spacing[5],
    boxShadow: shadows.sm,
    marginBottom: spacing[6],
  },
  headerIcon: {
    width: 42,
    height: 42,
    borderRadius: 10,
    background: colors.primaryLighter,
    color: colors.primary,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 18,
  },
  title: {
    fontSize: 20,
    color: colors.primary,
    fontWeight: 400,
    margin: 0,
  },
  subtitle: {
    margin: "4px 0 0",
    fontSize: 13,
    color: colors.gray600,
  },
  emptyState: {
    background: colors.bgCard,
    borderRadius: radius.md,
    padding: spacing[8],
    boxShadow: shadows.sm,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: spacing[3],
  },
  buildingCard: {
    background: colors.bgCard,
    borderRadius: radius.md,
    boxShadow: shadows.sm,
    overflow: "hidden",
  },
  buildingHeader: {
    width: "100%",
    background: "transparent",
    border: "none",
    padding: spacing[4],
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    cursor: "pointer",
    fontFamily: typography.fontFamily,
  },
  countBadge: {
    background: colors.primaryLighter,
    color: colors.primary,
    borderRadius: radius.full,
    padding: "1px 8px",
    fontSize: 11,
    minWidth: 18,
    textAlign: "center",
  },
  paymentsTable: {
    borderTop: `1px solid ${colors.gray100}`,
  },
  paymentRow: {
    display: "flex",
    alignItems: "center",
    padding: `${spacing[3]}px ${spacing[4]}px`,
    borderBottom: `1px solid ${colors.gray100}`,
    cursor: "pointer",
  },
  paymentDescription: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  cartBar: {
    position: "fixed",
    bottom: 16,
    left: "50%",
    transform: "translateX(-50%)",
    background: colors.bgCard,
    borderRadius: radius.full,
    boxShadow: shadows.lg,
    padding: "12px 20px",
    display: "flex",
    alignItems: "center",
    gap: 16,
    zIndex: 50,
  },
  btnPrimary: {
    background: colors.primary,
    color: "#fff",
    border: "none",
    borderRadius: radius.sm,
    padding: "8px 18px",
    fontSize: 14,
    cursor: "pointer",
    fontFamily: typography.fontFamily,
  },
  btnGhost: {
    background: "transparent",
    border: "none",
    color: colors.gray600,
    padding: "8px 14px",
    cursor: "pointer",
    fontSize: 13,
    fontFamily: typography.fontFamily,
  },
};

export default MisPagos;
